import React, { useCallback, useEffect, useRef, useState } from "react"
import type { Editor } from "@tiptap/react"
import { migrateMathStrings } from "@tiptap/extension-mathematics"
import { useTiptapEditor } from "../hooks/useTiptapEditor"
import { useImageUpload } from "../hooks/useImageUpload"
import { useDiagramClickEdit } from "../hooks/useDiagramClickEdit"
import {
  useDiagramEditorSession,
  getInitialDiagramContentType,
  getInitialDiagramXml,
} from "../hooks/useDiagramEditorSession"
import {
  MathEditMode,
  MathEditRequest,
  TiptapEditorProps,
} from "../types"
import EditorToolbar from "./EditorToolbar"
import EditorContentArea from "./EditorContentArea"
import EditorHtmlSource from "./EditorHtmlSource"
import { EditorPortalProvider } from "./EditorPortalContext"
import MathEditModal from "./MathEditModal"
import DrawioEditorModal from "./DrawioEditorModal"
import LinkInsertModal from "./LinkInsertModal"
import ImageInsertModal from "./ImageInsertModal"
import { formatHtml } from "../utils/formatHtml"
import { insertBlockContentAndPlaceCaretAfter } from "../utils/placeCaretAfterBlockInsert"
import { saveDiagramToEditor } from "../utils/saveDiagramToEditor"
import type { DiagramSavePayload } from "../utils/diagramNodeAttrs"
import { parseChemistryLatex } from "../utils/chemistryLatex"
import type { MathEditVariant } from "./MathEditModal"
import "../tiptapEditor.scss"

type ActiveMathModal = {
  mode: MathEditMode
  latex: string
  pos?: number
  variant: MathEditVariant
}

const LINK_MARK_ATTRS = {
  target: "_blank",
  rel: "noopener noreferrer",
} as const

const TiptapEditor = ({
  id,
  value = "",
  height = 400,
  label = "",
  placeholder = "Start writing…",
  editable = true,
  handleOnChange,
}: TiptapEditorProps) => {
  const fullscreenRef = useRef<HTMLDivElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mathModal, setMathModal] = useState<ActiveMathModal | null>(null)
  const diagramSession = useDiagramEditorSession()
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkModalUrl, setLinkModalUrl] = useState("")
  const [linkModalText, setLinkModalText] = useState("")
  const [linkModalIsEditing, setLinkModalIsEditing] = useState(false)
  const [linkModalRequireText, setLinkModalRequireText] = useState(false)
  const [isImageResizeEnabled, setIsImageResizeEnabled] = useState(false)
  const [isHtmlMode, setIsHtmlMode] = useState(false)
  const [htmlDraft, setHtmlDraft] = useState(value)

  useEffect(() => {
    setPortalRoot(portalRef.current)
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenRef.current)
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  const applyMathEdit = useCallback((editor: Editor | null, latex: string) => {
    if (!editor || !mathModal) return

    switch (mathModal.mode) {
      case "insert-inline":
        editor.chain().focus().insertInlineMath({ latex }).run()
        break
      case "insert-block":
        editor.chain().focus().insertBlockMath({ latex }).run()
        break
      case "edit-inline":
        if (mathModal.pos != null) {
          editor
            .chain()
            .setNodeSelection(mathModal.pos)
            .updateInlineMath({ latex })
            .focus()
            .run()
        }
        break
      case "edit-block":
        if (mathModal.pos != null) {
          editor
            .chain()
            .setNodeSelection(mathModal.pos)
            .updateBlockMath({ latex })
            .focus()
            .run()
        }
        break
      default:
        break
    }
  }, [mathModal])

  const handleMathEditRequest = useCallback((request: MathEditRequest) => {
    const parsed = parseChemistryLatex(request.latex)
    const isChemistry = request.isChemistry ?? parsed.isChemistry

    setMathModal({
      mode: request.mode,
      latex: isChemistry ? parsed.body : request.latex,
      pos: request.pos,
      variant: isChemistry ? "chemistry" : "math",
    })
  }, [])

  const handleDiagramEditRequest = useCallback(
    (request: Parameters<typeof diagramSession.openEdit>[0]) => {
      diagramSession.openEdit(request)
    },
    [diagramSession.openEdit]
  )

  const editor = useTiptapEditor({
    content: value,
    placeholder,
    editable,
    onChange: handleOnChange,
    onMathEditRequest: handleMathEditRequest,
  })

  useDiagramClickEdit(editor, editable, handleDiagramEditRequest)

  const {
    fileInputRef,
    isUploading,
    pendingImage,
    openFilePicker,
    handleFileChange,
    queueImageFile,
    clearPendingImage,
  } = useImageUpload()

  useEffect(() => {
    if (!editor) return

    const root = editor.view.dom

    const handleDragOver = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes("Files")) return
      event.preventDefault()
    }

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? [])
      const imageFile = files.find((file) => file.type.startsWith("image/"))
      if (!imageFile) return

      event.preventDefault()
      void queueImageFile(imageFile)
    }

    const handlePaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? [])
      const imageFile = files.find((file) => file.type.startsWith("image/"))
      if (!imageFile) return

      event.preventDefault()
      void queueImageFile(imageFile)
    }

    root.addEventListener("dragover", handleDragOver)
    root.addEventListener("drop", handleDrop)
    root.addEventListener("paste", handlePaste)

    return () => {
      root.removeEventListener("dragover", handleDragOver)
      root.removeEventListener("drop", handleDrop)
      root.removeEventListener("paste", handlePaste)
    }
  }, [editor, queueImageFile])

  const handleImageInsert = useCallback(
    (size: { width: number; height: number }) => {
      if (!editor || !pendingImage) return

      insertBlockContentAndPlaceCaretAfter(editor, {
        type: "image",
        attrs: {
          src: pendingImage.src,
          alt: pendingImage.alt,
          width: size.width,
          height: size.height,
        },
      })

      clearPendingImage()
    },
    [editor, pendingImage, clearPendingImage]
  )

  useEffect(() => {
    if (isHtmlMode) {
      setHtmlDraft(value)
    }
  }, [value, isHtmlMode])

  const handleToggleHtmlMode = async () => {
    if (!editor) return

    if (!isHtmlMode) {
      const formatted = await formatHtml(editor.getHTML())
      setHtmlDraft(formatted)
      handleOnChange?.(formatted)
      setIsHtmlMode(true)
      return
    }

    editor.commands.setContent(htmlDraft, { emitUpdate: false })
    migrateMathStrings(editor)
    const nextHtml = editor.getHTML()
    handleOnChange?.(nextHtml)
    setIsHtmlMode(false)
  }

  const handleHtmlDraftChange = (html: string) => {
    setHtmlDraft(html)
    handleOnChange?.(html)
  }

  const handleToggleFullscreen = async () => {
    const element = fullscreenRef.current
    if (!element) return

    try {
      if (document.fullscreenElement === element) {
        await document.exitFullscreen()
      } else {
        await element.requestFullscreen()
      }
    } catch {
      // Fullscreen may be blocked by the browser.
    }
  }

  const openInsertEquation = () => {
    setMathModal({
      mode: "insert-inline",
      latex: "",
      variant: "math",
    })
  }

  const openInsertDiagram = () => {
    diagramSession.openCreate()
  }

  const openInsertTable = () => {
    if (!editor) return

    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run()
  }

  const handleDiagramSave = (payload: DiagramSavePayload) => {
    if (!editor) return
    saveDiagramToEditor(editor, payload, diagramSession.session)
    diagramSession.close()
  }

  const openInsertLink = () => {
    if (!editor) return

    const { from, to, empty } = editor.state.selection
    const isEditing = editor.isActive("link")
    let initialText = ""
    let initialUrl = ""

    if (isEditing) {
      editor.chain().focus().extendMarkRange("link").run()
      const linkSelection = editor.state.selection
      initialText = editor.state.doc.textBetween(
        linkSelection.from,
        linkSelection.to
      )
      initialUrl = editor.getAttributes("link").href ?? ""
    } else {
      if (!empty) {
        initialText = editor.state.doc.textBetween(from, to)
      }
      const previousUrl = editor.getAttributes("link").href
      initialUrl = typeof previousUrl === "string" ? previousUrl : ""
    }

    setLinkModalUrl(initialUrl)
    setLinkModalText(initialText)
    setLinkModalIsEditing(isEditing)
    setLinkModalRequireText(empty && !isEditing)
    setLinkModalOpen(true)
  }

  const applyLinkToRange = (
    currentEditor: Editor,
    rangeFrom: number,
    rangeTo: number,
    href: string,
    displayText: string
  ) => {
    currentEditor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        if (!dispatch) return true

        tr.insertText(displayText, rangeFrom, rangeTo)
        tr.addMark(
          rangeFrom,
          rangeFrom + displayText.length,
          currentEditor.schema.marks.link.create({
            href,
            ...LINK_MARK_ATTRS,
          })
        )

        return true
      })
      .run()
  }

  const handleLinkSave = (url: string, text: string) => {
    if (!editor) return

    const trimmedUrl = url.trim()
    const trimmedText = text.trim()
    const { empty } = editor.state.selection
    const isEditing = editor.isActive("link")

    if (isEditing) {
      editor.chain().focus().extendMarkRange("link").run()
      const linkSelection = editor.state.selection
      const currentText = editor.state.doc.textBetween(
        linkSelection.from,
        linkSelection.to
      )
      applyLinkToRange(
        editor,
        linkSelection.from,
        linkSelection.to,
        trimmedUrl,
        trimmedText || currentText
      )
    } else if (!empty) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: trimmedUrl }).run()
    } else {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: trimmedText,
          marks: [
            {
              type: "link",
              attrs: {
                href: trimmedUrl,
                ...LINK_MARK_ATTRS,
              },
            },
          ],
        })
        .run()
    }

    setLinkModalOpen(false)
  }

  const handleLinkRemove = () => {
    if (!editor) return

    editor.chain().focus().extendMarkRange("link").unsetLink().run()
    setLinkModalOpen(false)
  }

  const mathModalTitle = (() => {
    if (!mathModal) return "Insert Equation"
    const isEdit = mathModal.mode.startsWith("edit")
    if (mathModal.variant === "chemistry") {
      return isEdit ? "Edit chemical formula" : "Insert chemical formula"
    }
    return isEdit ? "Edit equation" : "Insert equation"
  })()

  const contentHeight = isFullscreen ? "100%" : height

  return (
    <div className='tiptap-editor' id={id}>
      <div
        ref={fullscreenRef}
        className={`tiptap-editor__fullscreen-root${
          isFullscreen ? " is-fullscreen" : ""
        }`}
      >
        <EditorPortalProvider portalRoot={portalRoot}>
          {label && (
            <label className='tiptap-editor__label' htmlFor={id}>
              {label}
            </label>
          )}

          <div
            className={`tiptap-editor__shell${
              isImageResizeEnabled ? " is-image-resize-on" : " is-image-resize-off"
            }`}
          >
            <EditorToolbar
              editor={editor}
              isHtmlMode={isHtmlMode}
              isFullscreen={isFullscreen}
              isImageResizeEnabled={isImageResizeEnabled}
              isUploadingImage={isUploading}
              onToggleHtmlMode={handleToggleHtmlMode}
              onToggleFullscreen={handleToggleFullscreen}
              onToggleImageResize={() =>
                setIsImageResizeEnabled((current) => !current)
              }
              onInsertImage={openFilePicker}
              onInsertEquation={openInsertEquation}
              onInsertDiagram={openInsertDiagram}
              onInsertLink={openInsertLink}
              onInsertTable={openInsertTable}
            />
            <input
              ref={fileInputRef}
              type='file'
              accept='image/jpeg,image/png,image/gif,image/webp,image/svg+xml'
              className='tiptap-editor__image-input'
              onChange={handleFileChange}
              tabIndex={-1}
              aria-hidden
            />
            {isHtmlMode ? (
              <EditorHtmlSource
                value={htmlDraft}
                height={contentHeight}
                onChange={handleHtmlDraftChange}
              />
            ) : (
              <EditorContentArea editor={editor} height={contentHeight} />
            )}
          </div>

          {mathModal && (
            <MathEditModal
              isOpen
              title={mathModalTitle}
              initialLatex={mathModal.latex}
              variant={mathModal.variant}
              onClose={() => setMathModal(null)}
              onSave={(latex) => {
                applyMathEdit(editor, latex)
                setMathModal(null)
              }}
            />
          )}

          {diagramSession.isOpen && (
            <DrawioEditorModal
              isOpen
              mode={diagramSession.session?.mode ?? "create"}
              initialXml={getInitialDiagramXml(diagramSession.session)}
              initialContentType={getInitialDiagramContentType(
                diagramSession.session
              )}
              onClose={diagramSession.close}
              onSave={handleDiagramSave}
            />
          )}

          {pendingImage && (
            <ImageInsertModal
              image={pendingImage}
              onClose={clearPendingImage}
              onInsert={handleImageInsert}
            />
          )}

          {linkModalOpen && (
            <LinkInsertModal
              isOpen
              initialUrl={linkModalUrl}
              initialText={linkModalText}
              isEditing={linkModalIsEditing}
              requireText={linkModalRequireText}
              onClose={() => setLinkModalOpen(false)}
              onSave={handleLinkSave}
              onRemove={handleLinkRemove}
            />
          )}
        </EditorPortalProvider>

        <div ref={portalRef} className='tiptap-editor__portal-root' />
      </div>
    </div>
  )
}

export default TiptapEditor
